import { useEffect, useRef, useState } from 'react';

interface Dimensions {
  width: number;
  height: number;
}

interface UseContainerDimensionsOptions {
  defaultWidth?: number;
  defaultHeight?: number;
}

export function useContainerDimensions({
  defaultWidth = 400,
  defaultHeight = 200,
}: UseContainerDimensionsOptions = {}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: defaultWidth,
    height: defaultHeight,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Set initial dimensions
    const updateDimensions = () => {
      const rect = container.getBoundingClientRect();
      setDimensions({
        width: Math.round(rect.width) || defaultWidth,
        height: Math.round(rect.height) || defaultHeight,
      });
    };

    // Create ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({
          width: Math.round(width) || defaultWidth,
          height: Math.round(height) || defaultHeight,
        });
      }
    });

    // Start observing
    resizeObserver.observe(container);

    // Set initial dimensions
    updateDimensions();

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [defaultWidth, defaultHeight]);

  return {
    ref: containerRef,
    dimensions,
  };
}
